"use client";

import { m as motion } from "framer-motion";
import { Gauge, Code2, Rocket, Layers } from "lucide-react";

interface ServiziHeroVisualProps {
  isItalian: boolean;
}

// Three common ways in. Prices and weeks match services.ts.
const services = [
  {
    icon: Gauge,
    titleIt: "Velocizzare WordPress",
    titleEn: "WordPress speed",
    descIt: "Analisi, interventi e numeri prima e dopo. Da 600 €",
    descEn: "Analysis, fixes and numbers before and after. From €600",
    tagIt: "1-3 sett.",
    tagEn: "1-3 wks",
    accent: false,
  },
  {
    icon: Code2,
    titleIt: "Siti WordPress",
    titleEn: "WordPress sites",
    descIt: "Su misura, veloci, facili da aggiornare. Da 2200 €",
    descEn: "Custom, fast, easy to update. From €2200",
    tagIt: "3-6 sett.",
    tagEn: "3-6 wks",
    accent: false,
  },
  {
    icon: Rocket,
    titleIt: "MVP a prezzo fisso",
    titleEn: "Fixed-price MVP",
    descIt: "La prima versione del tuo software, con i pagamenti attivi. Da 4800 €",
    descEn: "The first version of your software, payments on. From €4800",
    tagIt: "4-6 sett.",
    tagEn: "4-6 wks",
    accent: true,
  },
];

export function ServiziHeroVisual({ isItalian }: ServiziHeroVisualProps) {
  return (
    <div className="relative w-full max-w-[360px] mx-auto pointer-events-none select-none">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-primary/8 blur-3xl" />

      <div className="relative space-y-3">
        {/* Header label */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.4 }}
          className="flex items-center justify-between mb-1"
        >
          <span className="text-[9px] font-mono text-muted-foreground/55 uppercase tracking-[0.12em]">
            {isItalian ? "Tre modi per iniziare" : "Three ways to start"}
          </span>
          <span className="text-[9px] font-mono text-primary">10 {isItalian ? "servizi" : "services"}</span>
        </motion.div>

        {/* Service cards */}
        {services.map((svc, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.12, duration: 0.45, ease: "easeOut" }}
            className={`rounded-xl border p-4 ${
              svc.accent
                ? "border-primary/30 bg-primary/5 shadow-[0_4px_24px_hsl(var(--primary)/0.12)]"
                : "border-[hsl(var(--border))] bg-surface"
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  svc.accent ? "bg-primary/20" : "bg-primary/10"
                }`}
              >
                <svc.icon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span
                    className={`text-[11px] font-semibold ${
                      svc.accent ? "text-foreground" : "text-foreground/80"
                    }`}
                  >
                    {isItalian ? svc.titleIt : svc.titleEn}
                  </span>
                  <span
                    className={`text-[9px] font-mono shrink-0 rounded-full px-2 py-0.5 ${
                      svc.accent
                        ? "bg-primary/15 text-primary"
                        : "bg-surface-hover text-muted-foreground/60"
                    }`}
                  >
                    {isItalian ? svc.tagIt : svc.tagEn}
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground/70 leading-snug">
                  {isItalian ? svc.descIt : svc.descEn}
                </p>
              </div>
            </div>
          </motion.div>
        ))}

        {/* Other services */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.4 }}
          className="flex items-center gap-2 rounded-xl border border-dashed border-[hsl(var(--border))] px-4 py-2.5"
        >
          <Layers className="w-3.5 h-3.5 text-primary/60 shrink-0" />
          <span className="text-[10px] text-muted-foreground/60">
            {isItalian
              ? "E poi ecommerce, web app, app mobile, automazioni, 3D e manutenzione"
              : "Plus ecommerce, web apps, mobile apps, automations, 3D and maintenance"}
          </span>
        </motion.div>
      </div>
    </div>
  );
}
